import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Lock } from 'lucide-react';
import { ApiService } from '../services/apiEndpoints';
import { useAuth } from '../context/AuthContext';
import ModernTextField from '../components/ui/ModernTextField';
import { requestFcmToken } from '../services/firebase'; // adjust path if needed
import './SignIn.css';

const SignIn = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) {
      setMessage("Email and password required");
      setIsError(true);
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const request = {
        email: email.trim(),
        password: password.trim()
      };

      const res = await ApiService.login(request);

if (res.status === 200 && res.data) {
        const id = res.data.studentId || "";
        const token = res.data.token || res.data.accessToken || res.data.jwt || null;

        // Persist before navigation so the session survives a hard-close even
        // if the operating system terminates the PWA immediately after login.
        await login({ id, studentData: res.data.studentData || null, token });

        // 👇 NEW: Request push notification permission and send token to backend
        requestFcmToken(id);

        // Navigate to the student dashboard
        navigate('/app/dashboard', { replace: true });
      }

    } catch (error) {
      setIsError(true);
      
      // Axios stores the HTTP error payload in error.response.data
      const errorData = error.response?.data;
      
      if (errorData) {
        const status = errorData.status;
        const err = errorData.error;

        switch (status) {
          case "verifying":
            setMessage("Your account is still under verification");
            break;
          case "rejected":
            setMessage("Your registration was rejected");
            break;
          case "kicked":
          case "banned":
            setMessage("You were banned. Contact admin");
            break;
          case "left":
            setMessage("You have left earlier. Ask admin to join back.");
            break;
          default:
            if (err === "wrong_password") {
              setMessage("Wrong password");
            } else if (err === "user_not_found") {
              setMessage("Account not found");
            } else {
              setMessage(err || "Login failed");
            }
            break;
        }
      } else {
        setMessage("Network error. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signin-screen">
      {/* Top Bar */}
      <div className="top-bar">
        <button onClick={() => navigate(-1)} className="icon-button">
          <ArrowLeft size={24} color="#1e3a8a" />
        </button>
      </div>

      <div className="signin-content">
        <img 
          src="/assets/logo.png" 
          alt="Utkarsh Logo" 
          className="signin-logo"
        />

        <h1 className="signin-title">Welcome Back!</h1>
        <p className="signin-subtitle">Login with your password</p>

        <div className="form-container">
          <ModernTextField 
            label="Email Address" 
            value={email} 
            onChange={(v) => { setEmail(v); setMessage(""); }} 
            icon={Mail} 
            type="email" 
          />
          
          <ModernTextField 
            label="Password" 
            value={password} 
            onChange={(v) => { setPassword(v); setMessage(""); }} 
            icon={Lock} 
            type="password" 
          />

          {message && (
            <p className={`status-message ${isError ? 'error' : 'success'}`}>
              {message}
            </p>
          )}

          <button 
            className="submit-button"
            onClick={handleLogin}
            disabled={loading}
            style={{ marginTop: '24px' }}
          >
            {loading ? <div className="spinner"></div> : "Login"}
          </button>

          <button className="guest-link" type="button" onClick={() => navigate('/app/register')}>
            Don't have an account? Register
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
